import Layers from "slotworks/graphics/layers/layers";
import AnimatedSprite from "slotworks/graphics/animation/animated-sprite";
import Components from "slotworks/components/components";
import SymbolComponent from "slotworks/components/matrix/symbol/symbol-component";
import isMcSymbol from "utils/is-mc-symbol";
import Contract from "slotworks/utils/contracts/contract";
import Sequence from "slotworks/utils/contracts/sequence";
import lightningBorderAnimationUtil from "utils/lightning-border-animation-util";
import Parallel from "slotworks/utils/contracts/parallel";
import { MultiStyleText } from "slotworks/graphics/pixi/pixi-multistyle-text";
import { Tween } from "slotworks/utils/tween";
import { Easing } from "slotworks/utils/tween";
import gameState from "slotworks/model/game-state";
import { JackpotTriggertrailResult } from "ags/integration/model/results/jackpot-triggertrail-result";
import { JackpotOptions } from "ags/components/jackpot-component";
import Logger from "slotworks/utils/logger";
import { JackpotTiers } from "ags/names";
import { Services } from "slotworks/services/services";
import { LLCustomLayers } from "names";
import betModel from "slotworks/model/bets/bet-model";
import { asArray } from "slotworks/utils/collection-utils";

class MCRewardAnimation {

    private layer: Layers;
    private borderEndAnimation: AnimatedSprite;
    private jackpotOptions: JackpotOptions;

    private spark: AnimatedSprite;

    private winnings: MultiStyleText;
    private winningsClone: MultiStyleText;

    private currentWinnings = 0;

    private defaultHitSound = "fortune_blast_tally_hit1";

    private jackpotSounds = new Map<JackpotTiers, string>([
        [JackpotTiers.MINI, this.defaultHitSound],
        [JackpotTiers.MINOR, "fortune_blast_tally_hit2"],
        [JackpotTiers.MAJOR, "fortune_blast_tally_hit3"],
        [JackpotTiers.GRAND, "fortune_blast_tally_hit4"]
    ] as any);

    public setup() {
        this.storeJackpotValues();
        this.layer = Layers.Custom.get(LLCustomLayers.HOLD_AND_SPIN_BORDER);

        this.borderEndAnimation = this.layer.getAnimatedSprite("bonus_symbol_frame_end");
        this.borderEndAnimation.visible = false;
        this.borderEndAnimation.anchor.set(0.5, 0.5);

        this.spark = this.layer.getAnimatedSprite("bonus_tally_particle");
        this.spark.visible = false;
    }

    public play() {

        // return Contract.empty();

        Layers.Custom.get(LLCustomLayers.JACKPOTS).setScene("bonus_tally").execute();
        Layers.Custom.get(LLCustomLayers.LOGOS).setScene("bonus_tally").execute();

        const bonusLayer = Layers.Custom.get(LLCustomLayers.JACKPOTS);
        this.winnings = bonusLayer.getText("summary_text");
        this.winningsClone = bonusLayer.getText("summary_text_clone");

        this.winnings.text = "";
        this.winningsClone.text = "";

        this.winningsClone.visible = false;

        this.currentWinnings = 0;

        const animations = this.getAllMCSymbols().map((symbol) => {
            return () => this.getAnimationSequence(symbol);
        });

        return new Sequence(animations);
    }

    private getAnimationSequence(symbol: SymbolComponent) {

        // const chaChingDelay = this.isOverChaChingThreshold(symbol) ? 500 : 0;

        return new Sequence([
            () => this.stopBorderBehaviour(symbol),
            () => this.playCollapseAnimation(symbol),
            () => this.animateSpark(symbol),
            () => new Parallel([
                () => this.playFrameAnimation(symbol),
                () => this.shakeAndUpdateWinnings(symbol)
            ])
            // () => Contract.getTimeoutContract(chaChingDelay),
        ]);
    }

    private playFrameAnimation(symbol: SymbolComponent) {
        const tier = 1;
        const layer = Layers.Custom.get(LLCustomLayers.JACKPOTS);
        const animation = layer.getAnimatedSprite(`bonus_tally_highlight_${tier}`);
        return this.showAndPlayAnimation(animation);
    }

    private shakeAndUpdateWinnings(symbol: SymbolComponent) {

        const originalScale = this.winningsClone.scale.x;

        this.currentWinnings += this.getMCSymbolValue(symbol);
        this.winnings.text = "" + Services.currency.format(this.currentWinnings, false, false);
        this.winningsClone.text = "" + Services.currency.format(this.currentWinnings, false, false);

        return new Sequence([
            () => this.shakeText(this.winnings),
            () => this.fadeZoomText(this.winningsClone, symbol),
            () => Contract.wrap(() => this.winningsClone.scale.set(originalScale, originalScale))
        ]);
    }

    private fadeZoomText(text: MultiStyleText, symbol: SymbolComponent) {

        const duration = 200;
        text.visible = true;
        text.alpha = 1;

        const hitSoundEvent = this.getHitSoundEvent(symbol);
        Services.sound.customEvent(hitSoundEvent);

        return new Parallel([
            () => new Contract((resolve) => {
                const outTween = new Tween(text.scale);
                outTween.to({ x: 1.3, y: 1.3 }, duration);
                outTween.easing(Easing.Sinusoidal.Out);
                outTween.onComplete(() => resolve());
                outTween.start();
            }),
            () => new Contract((resolve) => {
                const outTween = new Tween(text);
                outTween.to({ alpha: 0 }, duration);
                outTween.easing(Easing.Sinusoidal.Out);
                outTween.onComplete(() => resolve());
                outTween.start();
            })
        ]);
    }

    private shakeText(text: MultiStyleText) {
        return new Contract<void>((resolve) => {
            const outTween = new Tween(text.scale);
            outTween.to({ x: 1.1, y: 1.1 }, 100);
            outTween.onComplete(() => {
                const inTween = new Tween(text.scale);
                inTween.to({ x: 1, y: 1 }, 100);
                outTween.easing(Easing.Sinusoidal.InOut);
                inTween.onComplete(() => {
                    resolve();
                });
                inTween.start();
            });
            outTween.start();
        });
    }

    private playCollapseAnimation(symbol: SymbolComponent) {
        this.alignToSymbol(this.borderEndAnimation, symbol);
        this.showAndPlayAnimation(this.borderEndAnimation).execute();
        return Contract.getTimeoutContract(900);
    }

    private animateSpark(symbol: SymbolComponent) {

        return new Contract((resolve) => {
            const target = this.layer.getPosition("spark_target");
            const position = this.getSymbolPosition(symbol);
            this.spark.anchor.set(0.5, 0.5);

            const x = (position.x + position.width / 2);
            const y = (position.y + position.height / 2);

            Services.sound.customEvent("fortune_blast_tally_shoot");

            this.spark.x = x;
            this.spark.y = y;

            const tween = new Tween(this.spark);
            tween.to({ x: target.x, y: target.y }, 300);
            tween.easing(Easing.Sinusoidal.In);

            tween.start();
            this.spark.visible = true;
            this.spark.loop = true;
            this.spark.gotoAndPlay();

            tween.onComplete(() => {
                resolve();
                this.spark.visible = false;
            });
        });
    }

    private showAndPlayAnimation(animation: AnimatedSprite) {
        return new Contract<void>((resolve) => {
            animation.visible = true;
            animation.currentFrame = 0;

            animation.onComplete.addOnce(() => {
                animation.visible = false;
                resolve();
            });

            animation.play();
        });
    }

    private getSymbolPosition(symbol: SymbolComponent) {
        const symbolPosition = `symbol_${symbol.gridPosition.x}_${symbol.gridPosition.y}`;
        return Layers.MatrixContent.getPosition(symbolPosition);
    }

    private alignToSymbol(animation: AnimatedSprite, symbol: SymbolComponent) {
        const position = this.getSymbolPosition(symbol);

        animation.x = position.x + position.width / 2;
        animation.y = position.y + position.height / 2;
    }

    private stopBorderBehaviour(symbol: SymbolComponent) {
        return Contract.wrap(() => lightningBorderAnimationUtil.stopForSymbol(symbol));
    }

    private getAllMCSymbols() {
        const allSymbols = Components.matrix.getGridSymbols();
        return allSymbols.filter((symbol) => isMcSymbol(symbol.symbolId));
    }

    private storeJackpotValues() {
        const initRecord = gameState.getCurrentGame().getRootRecord();
        const jackpotResult = initRecord.getResultsOfType(JackpotTriggertrailResult)[0];
        this.jackpotOptions = new JackpotOptions(jackpotResult.jackpots[3], jackpotResult.jackpots[2], jackpotResult.jackpots[1], jackpotResult.jackpots[0]);
    }

    private isOverChaChingThreshold(symbol: SymbolComponent) {
        const symbolValue = this.getMCSymbolValue(symbol);
        return symbolValue >= 10000;
    }

    private getHitSoundEvent(symbol: SymbolComponent) {
        const jackpotTier = this.getJackpotTier(symbol);
        return this.jackpotSounds.get(jackpotTier) || this.defaultHitSound;
    }

    private getMcSymbolText(symbol: SymbolComponent) {
        if (!isMcSymbol(symbol.symbolId)) {
            Logger.warn("Requested MC symbol value for a non MC symbol.");
        }

        return symbol.symbolId.split("_")[1];
    }

    private getJackpotTier(symbol: SymbolComponent) {
        const stringSymbolValue = this.getMcSymbolText(symbol);
        const jackpotTier = stringSymbolValue.toString();

        switch (jackpotTier) {
            case "GRAND":
                return JackpotTiers.GRAND;
            case "MAJOR":
                return JackpotTiers.MAJOR;
            case "MINI":
                return JackpotTiers.MINI;
            case "MINOR":
                return JackpotTiers.MINOR;
            default:
                return null;
        }
    }

    private getMCSymbolValue(symbol: SymbolComponent) {

        const stringSymbolValue = this.getMcSymbolText(symbol);
        const numericSymbolValue = parseInt(stringSymbolValue);
        const jackpotTier = this.getJackpotTier(symbol);

        const symbolValue = this.getJackpotAmount(jackpotTier) || numericSymbolValue;

        return symbolValue * betModel.getCreditSize();
    }

    private getJackpotAmount(jackpotTier: JackpotTiers) {
        const betIndex = betModel.creditsPerLine.values.indexOf(betModel.creditsPerLine.currentValue);
        const amount = asArray(this.jackpotOptions.getForTier(jackpotTier))[betIndex];
        return amount;
    }
}

export default new MCRewardAnimation();
